import React, { useState, useEffect, useMemo, useRef, useCallback, ReactElement, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { VariableSizeList as List, ListChildComponentProps, ListOnItemsRenderedProps } from 'react-window';
import { actions as locationDashboardActionsSource } from '@Actions/locationDashboardActions';
import { OverviewLocationFilters, LocationBreakdownRow } from '@Apis/companyDashboard';
import AssignTasksDrawer from '@Components/LocationDashboard/Widgets/AssignTasksDrawer';
import { LocationSummaryData } from '../types';
import LocationSummaryTile, { expandToggleId } from './Tiles/LocationSummaryTile';
import LocationSummaryTileSkeleton from './Tiles/LocationSummaryTileSkeleton';
import LocationBreakdownPanel, { BreakdownView, DEFAULT_BREAKDOWN_VIEW } from './LocationBreakdownPanel';
import { BreakdownSelection, BreakdownMetric } from './breakdownSelection';
import './LocationsList.scss';

// Single source of truth for the tile height: published to CSS as --lst-tile-height and
// consumed by LocationSummaryTile.scss, so the virtualized row size and the rendered tile
// can never drift apart.
const TILE_HEIGHT = 78;
const ROW_GAP = 8;
const ROW_HEIGHT = TILE_HEIGHT + ROW_GAP;

// An expanded row draws the card border itself (see .overview-location-row--expanded in
// LocationsList.scss). The measured panel height is the panel content's offsetHeight only, so
// the virtualized row size has to reserve those two hairlines or the panel is clipped.
const EXPANDED_ROW_BORDER = 2;
const TILE_HEIGHT_VARS = { '--lst-tile-height': `${TILE_HEIGHT}px` } as React.CSSProperties;

// Must match the transition duration on .overview-location-row__panel in LocationsList.scss:
// the collapsing panel stays mounted for exactly this long so it has something to animate.
const PANEL_ANIMATION_MS = 220;

// Below this many locations the list renders every row. Above it only the rows near the
// viewport mount — a tab covering many projects can resolve to hundreds of locations, and every
// tile mounts a stacked-bar graph. Either way the list is laid out at its full height and the
// page scrolls it; the virtualized list never gets a scrollbar of its own.
const VIRTUALIZE_THRESHOLD = 25;

// Rows mounted beyond each edge of the viewport, so a fast scroll does not flash empty space.
const VIRTUAL_OVERSCAN_COUNT = 4;
const VIRTUAL_LIST_STYLE: React.CSSProperties = { height: 'auto', overflow: 'visible' };

// Placeholder row count shown while the first fetch is in flight — enough to fill the
// fold on a typical monitor without over-committing on a short list once data lands.
const SKELETON_ROW_COUNT = 6;

// How far down the loaded rows the user has to scroll before the next page is requested —
// early enough that it has usually landed by the time they reach the bottom.
const LOAD_MORE_THRESHOLD = 0.8;

interface LocationsListProps {
    locations: LocationSummaryData[];
    onLocationChange: (id: number) => void;
    /** A group inside an expanded breakdown panel was clicked. */
    onBreakdownGroupSelect: (selection: BreakdownSelection) => void;
    isLoadingMetrics?: boolean;
    /** Which configurable Overview tab this list belongs to, so the breakdown panel scopes to it. */
    tabId: number;
    /**
     * The page's active filters. They scope the breakdown panel's fetch, and their
     * `projectIds` are the selection every tile drill-down carries to the Tasks page.
     */
    filters: OverviewLocationFilters;
    /** Opens the page's filters drawer, from the breakdown panel's filter-state message. */
    onOpenFilters?: () => void;
    /**
     * The only difference between the Locations Summary and Locations Summary Expandable
     * widgets: false drops the per-row caret and the breakdown panel behind it, leaving rows
     * that only select. Everything else — virtualization, filters, the detail sidebar — is shared.
     */
    isExpandable?: boolean;
    /** More locations exist on the server than are in `locations`. */
    hasMore?: boolean;
    /** The next page is in flight. */
    isLoadingMore?: boolean;
    /** Asks for the next page; called as the user scrolls towards the end of the loaded rows. */
    onLoadMore?: () => void;
    /** The last next-page request failed. */
    hasLoadMoreError?: boolean;
    /**
     * Distance in px from the top of the list to the top of the selected row, so the detail
     * card beside the list can line up with the location it is showing.
     */
    onActiveRowOffsetChange?: (offset: number) => void;
    /** Tasks were saved from a tile's Assign Tasks drawer, so its counts are out of date. */
    onAssignTasksComplete?: () => void;
}

interface VirtualRowData {
    locations: LocationSummaryData[];
    renderRow: (location: LocationSummaryData) => ReactNode;
}

const VirtualRow = ({ data, index, style }: ListChildComponentProps<VirtualRowData>): ReactElement => (
    <div style={{ ...style, height: (style.height as number) - ROW_GAP }}>
        {data.renderRow(data.locations[index])}
    </div>
);

const LocationsList = ({
    locations,
    onLocationChange,
    onBreakdownGroupSelect,
    isLoadingMetrics,
    tabId,
    filters,
    onOpenFilters,
    isExpandable = true,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    hasLoadMoreError = false,
    onActiveRowOffsetChange,
    onAssignTasksComplete,
}: LocationsListProps): ReactElement => {

    const selectedProjectIds = filters.projectIds;

    const [activeLocationId, setActiveLocationId] = useState<number>(0);
    // Owned here rather than inside the tile: virtualized rows unmount when scrolled out
    // of the window, which would tear down an open drawer mid-edit.
    const [assignTasksLocationKey, setAssignTasksLocationKey] = useState<string | null>(null);

    const [expandedLocationId, setExpandedLocationId] = useState<number | null>(null);
    const [closingLocationId, setClosingLocationId] = useState<number | null>(null);
    const [breakdownView, setBreakdownView] = useState<BreakdownView>(DEFAULT_BREAKDOWN_VIEW);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dispatch = useDispatch();
    const locationDashboardActions = useMemo(
        () => bindActionCreators(locationDashboardActionsSource, dispatch),
        [dispatch]
    );

    const lastLocationIds = useRef<number[] | null>(null);

    // This effect throws away the user's selection, so it has to fire when the *set* of
    // locations changes, not merely when the array is rebuilt. The overview-tab query refetches
    // on mount and on every filter tweak, and each response is a fresh array — without this
    // guard a background refetch that only moved a metric would collapse the open breakdown
    // panel, drop any drill-down and snap the selection back to the first row mid-read.
    // The next page arriving is left alone too, but only as an exact in-order append: a filter
    // change that widens the set slots its new rows in among the old ones, and that is a new
    // list the user did not scroll to.
    useEffect(() => {
        const ids = locations.map((location) => location.id);
        const previous = lastLocationIds.current;
        lastLocationIds.current = ids;
        if (previous !== null) {
            const idSet = new Set(ids);
            const isSameSet = previous.length === ids.length && previous.every((id) => idSet.has(id));
            const isAppend = previous.length > 0
                && previous.length < ids.length
                && previous.every((id, index) => ids[index] === id);
            if (isSameSet || isAppend) return;
        }

        setExpandedLocationId(null);
        setClosingLocationId(null);

        // Set the first location as active by default - if available
        if (locations.length > 0) {
            setActiveLocationId(locations[0].id);
            onLocationChange(locations[0].id);
            return;
        }
        setActiveLocationId(0);
        onLocationChange(0);
    }, [locations, onLocationChange]);

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    const onLocationClick = useCallback((id: number) => {
        setActiveLocationId(id);
        onLocationChange(id);
    }, [onLocationChange]);

    // Drilling into a group also makes its location the active one, so the sidebar the
    // breadcrumb points back to is the row the user is looking at.
    const onGroupSelect = useCallback((location: LocationSummaryData, row: LocationBreakdownRow, metric: BreakdownMetric) => {
        setActiveLocationId(location.id);
        onBreakdownGroupSelect({
            locationId: location.id,
            locationKey: location.locationKey,
            locationName: location.name,
            view: breakdownView,
            groupId: row.groupId,
            groupName: row.groupName,
            metric,
        });
    }, [breakdownView, onBreakdownGroupSelect]);

    const [panelHeight, setPanelHeight] = useState(0);
    const [closingPanelHeight, setClosingPanelHeight] = useState(0);
    const latestPanelHeight = useRef(0);
    const panelResizeObserver = useRef<ResizeObserver | null>(null);

    const recordPanelHeight = useCallback((height: number) => {
        latestPanelHeight.current = height;
        setPanelHeight(height);
    }, []);

    const startClosing = useCallback((id: number) => {
        setClosingLocationId(id);
        setClosingPanelHeight(latestPanelHeight.current);
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setClosingLocationId(null), PANEL_ANIMATION_MS);
    }, []);

    const toggleExpanded = useCallback((id: number) => {
        if (!isExpandable) return;
        const next = expandedLocationId === id ? null : id;
        if (expandedLocationId !== null && expandedLocationId !== next) {
            startClosing(expandedLocationId);
        }
        setExpandedLocationId(next);
    }, [isExpandable, expandedLocationId, startClosing]);

    const collapse = useCallback(() => {
        if (expandedLocationId === null) return;
        startClosing(expandedLocationId);
        setExpandedLocationId(null);
        document.getElementById(expandToggleId(expandedLocationId))?.focus();
    }, [expandedLocationId, startClosing]);

    const measurePanelRef = useCallback((node: HTMLDivElement | null) => {
        panelResizeObserver.current?.disconnect();
        panelResizeObserver.current = null;

        if (!node) return;

        recordPanelHeight(node.offsetHeight);

        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => recordPanelHeight(node.offsetHeight));
        observer.observe(node);
        panelResizeObserver.current = observer;
    }, [recordPanelHeight]);

    useEffect(() => () => panelResizeObserver.current?.disconnect(), []);

    useEffect(() => {
        if (expandedLocationId !== null || closingLocationId !== null) return;
        recordPanelHeight(0);
        setClosingPanelHeight(0);
    }, [expandedLocationId, closingLocationId, recordPanelHeight]);

    const closeAssignTasks = () => setAssignTasksLocationKey(null);
    const completeAssignTasks = () => {
        closeAssignTasks();
        onAssignTasksComplete?.();
    };

    // Nothing to show a real row for yet: `locations` is derived entirely from the metrics
    // fetch, so it is empty for the whole span of the initial load, not just an instant.
    const isInitialLoading = !!isLoadingMetrics && locations.length === 0;

    // The next page is requested from the virtual list's scroll position, so a list with more to
    // load always virtualizes — otherwise a page no longer than the threshold could never grow.
    const isVirtualized = locations.length > VIRTUALIZE_THRESHOLD || hasMore;

    const renderRow = useCallback((location: LocationSummaryData) => {
        const isExpanded = isExpandable && expandedLocationId === location.id;
        const isClosing = isExpandable && closingLocationId === location.id;
        const isActive = activeLocationId === location.id;

        // The expanded row draws the selection border in place of the tile, so it needs the
        // selected state as a class of its own — see the note in LocationsList.scss.
        const rowClassName = [
            'overview-location-row',
            isExpanded ? 'overview-location-row--expanded' : '',
            isActive ? 'overview-location-row--active' : '',
        ].filter(Boolean).join(' ');

        return (
            <div className={rowClassName}>
                <LocationSummaryTile
                    location={location}
                    onClick={() => onLocationClick(location.id)}
                    isActive={isActive}
                    isLoadingMetrics={isLoadingMetrics}
                    onAssignTasks={() => setAssignTasksLocationKey(location.locationKey)}
                    selectedProjectIds={selectedProjectIds}
                    isExpanded={isExpanded}
                    // Undefined, not a no-op: the tile takes the absent handler as the signal
                    // to leave the caret out of the row entirely.
                    onToggleExpand={isExpandable ? () => toggleExpanded(location.id) : undefined}
                />
                {isExpandable && (
                    <div className='overview-location-row__panel'>
                        <div
                            className='overview-location-row__panel-content'
                            ref={isExpanded ? measurePanelRef : undefined}>
                            {(isExpanded || isClosing) && (
                                <LocationBreakdownPanel
                                    locationName={location.name}
                                    locationKey={location.locationKey}
                                    tabId={tabId}
                                    filters={filters}
                                    view={breakdownView}
                                    onViewChange={setBreakdownView}
                                    onClose={collapse}
                                    isExpanded={isExpanded}
                                    onGroupSelect={(row, metric) => onGroupSelect(location, row, metric)}
                                    onOpenFilters={onOpenFilters}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }, [
        isExpandable, expandedLocationId, closingLocationId, breakdownView, activeLocationId,
        isLoadingMetrics, selectedProjectIds, tabId, filters, onLocationClick, onGroupSelect, toggleExpanded,
        collapse, measurePanelRef, onOpenFilters,
    ]);

    const assignTasksDrawer = assignTasksLocationKey !== null && (
        <AssignTasksDrawer
            locationKey={assignTasksLocationKey}
            locationDashboardActions={locationDashboardActions}
            onComplete={completeAssignTasks}
            onCancel={closeAssignTasks}
        />
    );

    const listRef = useRef<List>(null);
    const listOuterRef = useRef<HTMLDivElement>(null);
    const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
    const virtualItemData = useMemo<VirtualRowData>(() => ({ locations, renderRow }), [locations, renderRow]);

    const getItemSize = useCallback(
        (index: number) => {
            const id = locations[index]?.id;
            if (id === undefined || !isExpandable) return ROW_HEIGHT;
            if (id === expandedLocationId) return ROW_HEIGHT + panelHeight + EXPANDED_ROW_BORDER;
            if (id === closingLocationId) return ROW_HEIGHT + closingPanelHeight;
            return ROW_HEIGHT;
        },
        [locations, isExpandable, expandedLocationId, closingLocationId, panelHeight, closingPanelHeight]
    );

    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [getItemSize]);

    // Measured from the row sizes rather than the DOM: a virtualized row far from the viewport
    // is not mounted, and the inline list lays its rows out at the same sizes.
    const activeRowOffset = useMemo(() => {
        const index = locations.findIndex((location) => location.id === activeLocationId);
        let offset = 0;
        for (let i = 0; i < index; i++) offset += getItemSize(i);
        return offset;
    }, [locations, activeLocationId, getItemSize]);

    useEffect(() => {
        onActiveRowOffsetChange?.(activeRowOffset);
    }, [activeRowOffset, onActiveRowOffsetChange]);

    // The virtual list is as tall as all of its rows, so it never scrolls itself — the page
    // does. react-window only works out which rows to mount from its own scroll offset, so that
    // offset is fed from how far the list's top has moved above the viewport. Listening in the
    // capture phase on the document catches the scroll whichever element owns the page scroll.
    useEffect(() => {
        if (!isVirtualized || isInitialLoading) return undefined;

        const sync = () => {
            const outer = listOuterRef.current;
            if (!outer) return;
            setViewportHeight(window.innerHeight);
            listRef.current?.scrollTo(Math.max(0, -outer.getBoundingClientRect().top));
        };

        sync();
        document.addEventListener('scroll', sync, { capture: true, passive: true });
        window.addEventListener('resize', sync);
        return () => {
            document.removeEventListener('scroll', sync, { capture: true });
            window.removeEventListener('resize', sync);
        };
    }, [isVirtualized, isInitialLoading]);

    const onItemsRendered = useCallback(
        ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
            if (!hasMore || isLoadingMore || !onLoadMore) return;
            if (visibleStopIndex + 1 >= Math.floor(locations.length * LOAD_MORE_THRESHOLD)) onLoadMore();
        },
        [hasMore, isLoadingMore, onLoadMore, locations.length]
    );

    if (isInitialLoading) {
        return (
            <div className='overview-locations-list' style={TILE_HEIGHT_VARS}>
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                    <LocationSummaryTileSkeleton key={index} />
                ))}
            </div>
        );
    }

    if (isVirtualized) {
        return (
            <div className='overview-locations-list overview-locations-list--virtual' style={TILE_HEIGHT_VARS}>
                <List
                    ref={listRef}
                    outerRef={listOuterRef}
                    // Only the window react-window mounts rows for, not the element's height:
                    // `style` lets the list grow to its full content height instead.
                    height={viewportHeight}
                    style={VIRTUAL_LIST_STYLE}
                    overscanCount={VIRTUAL_OVERSCAN_COUNT}
                    itemCount={locations.length}
                    itemSize={getItemSize}
                    estimatedItemSize={ROW_HEIGHT}
                    width={'100%'}
                    itemData={virtualItemData}
                    itemKey={(index, data) => data.locations[index].id}
                    onItemsRendered={onItemsRendered}>
                    {VirtualRow}
                </List>
                {isLoadingMore && (
                    <div className='overview-locations-list__loading-more' role='status' aria-label='Loading more locations'>
                        <LocationSummaryTileSkeleton />
                    </div>
                )}
                {/* Scrolling is what asks for the next page, so after a failure a list short
                    enough not to scroll would otherwise have no way to ask again. */}
                {hasLoadMoreError && hasMore && !isLoadingMore && (
                    <div className='overview-locations-list__load-more-error' role='alert'>
                        <span>Couldn&apos;t load more locations.</span>
                        <button type='button' onClick={onLoadMore}>Retry</button>
                    </div>
                )}
                {assignTasksDrawer}
            </div>
        );
    }

    return (
        <div className='overview-locations-list' style={TILE_HEIGHT_VARS}>
            {locations.map((location) => (
                <React.Fragment key={location.id}>{renderRow(location)}</React.Fragment>
            ))}
            {assignTasksDrawer}
        </div>
    );
};

export default LocationsList;
